import ExpenseService from "@/services/api/expense";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import {
    createAsyncThunk,
    createSlice,
    isFulfilled,
    isRejected,
    isPending,
} from "@reduxjs/toolkit"

interface LoadingState {
    expenseClaims: boolean;
    expenseReport: boolean;
    myClaims: boolean;
    logHistory: boolean;
    expenseList : boolean;
}

interface ExpenseState {
    expenseClaims: any[],
    expenseReport: any,
    category: any[],
    myClaims: any[],
    history: any[],
    categoryList: any[],
    expenseApproval: any,
    isFetching: boolean;
    error: string | null;
    refreshCustomerDetails: boolean,
    refreshCustomerList: boolean
    loading: LoadingState
}

const initialState: ExpenseState = {
    expenseClaims: [],
    expenseReport: null,
    category: [],
    myClaims: [],
    history: [],
    categoryList: [],
    expenseApproval: null,
    loading: {
        expenseClaims: false,
        expenseReport: false,
        myClaims: false,
        logHistory: false,
        expenseList: false
    },
    isFetching: false,
    error: null,
    refreshCustomerDetails: false,
    refreshCustomerList: false,
}

export const getExpenseAdminClaims = createAsyncThunk(
    "expense/adminClaims",
    async (data: { payload: any, isSilentRefresh: boolean }, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.getExpenseAdminClaims(data.payload);
            return {
                data: handleResponse(res, rejectWithValue),
                isSilentRefresh: data.isSilentRefresh,
            };
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch Admin Claims");
        }
    }
)

export const getExpenseReport = createAsyncThunk(
    "expense/report",
    async (id: any, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.getExpenseReport(id);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch expense report");
        }
    }
)

export const approval = createAsyncThunk(
    "report/approve",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.approval(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || err?.response?.data?.message || "Failed to process approval or rejection");
        }
    }
)

export const getCategory = createAsyncThunk(
    "expense/category",
    async (_, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.getCategory();
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch category");
        }
    }
)

export const getExpenseMyClaims = createAsyncThunk(
    "expense/myClaims",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.getExpenseMyClaims(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch my claims");
        }
    }
)

export const getHistory = createAsyncThunk(
    "expense/history",
    async (id: number, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.getHistory(id);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch history")
        }
    }
)

export const addClaim = createAsyncThunk(
    "expense/addClaim",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.addClaim(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || err?.response?.data?.message || "Failed to add or update claim");
        }
    }
)

export const editClaim = createAsyncThunk(
    "expense/editClaim",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.editClaim(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || err?.response?.data?.message || "Failed to update claim");
        }
    }
)

export const takeAction = createAsyncThunk(
    "expense/takeAction",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.takeAction(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || err?.response?.data?.message || "Action failed");
        }
    }
)

export const expenseExport = createAsyncThunk(
    "expense/export",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.expenseExport(payload);
            return saveAsExcelFile(res, "Expense");
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || err?.response?.data?.message || "Failed to export");
        }
    }
)

export const expenseDelete = createAsyncThunk(
    "expense/delete",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.expenseDelete(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || err?.response?.data?.message || "Failed to Delete");
        }
    }
)

export const getExpenseCategory = createAsyncThunk(
    "expense/categoryList",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await ExpenseService.getExpenseCategory(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch Category");
        }
    }
)

const expenseSlice = createSlice({
    name: "expense",
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(getExpenseAdminClaims.pending, (state, action) => {
                if (!action.meta.arg.isSilentRefresh) {
                    state.loading.expenseClaims = true;
                }
            }) 
            .addCase(getExpenseAdminClaims.fulfilled, (state, action) => {
                state.expenseClaims = action.payload?.data || [];
                state.loading.expenseClaims = false;
            })
            .addCase(getExpenseAdminClaims.rejected, (state) => {
                state.loading.expenseClaims = false;
            })

            .addCase(getExpenseReport.pending, (state) => {
                state.expenseReport = null;
                state.loading.expenseReport = true;
            })
            .addCase(getExpenseReport.fulfilled, (state, action) => {
                state.expenseReport = action.payload || null;
                state.loading.expenseReport = false;
            })
            .addCase(getExpenseReport.rejected, (state, action) => {
                state.error = action.payload as string;
                state.loading.expenseReport = false;
            })

            .addCase(approval.fulfilled, (state, action) => {
                state.expenseApproval = action.payload || null;
            })
            .addCase(getCategory.fulfilled, (state, action) => {
                state.category = action.payload || null;
            })

            .addCase(getExpenseMyClaims.pending, (state) => {
                state.myClaims = [];
                state.loading.myClaims = true;
            })
            .addCase(getExpenseMyClaims.fulfilled, (state, action) => {
                state.myClaims = action.payload || [];
                state.loading.myClaims = false;
            })
            .addCase(getExpenseMyClaims.rejected, (state) => {
                state.loading.myClaims = false;
            })
            .addCase(getHistory.pending, (state) => {
                state.loading.logHistory = true;
                state.history = [];
            })
            .addCase(getHistory.fulfilled, (state, action) => {
                state.loading.logHistory = false;
                state.history = action.payload || [];
            })
            .addCase(getHistory.rejected, (state, action) => {
                state.loading.logHistory = false;
                state.history = [];
                state.error = action.payload as string;
            })
            .addCase(getExpenseCategory.pending, (state) => {
                state.loading.expenseList = true;
                state.categoryList = [];
            })
            .addCase(getExpenseCategory.fulfilled, (state, action) => {
                state.loading.expenseList = false;
                state.categoryList = action.payload || [];
            })
            .addCase(getExpenseCategory.rejected, (state,action) => {
                state.loading.expenseList = false;
                state.categoryList = [];
                state.error = action.payload as string;
            })
        builder
            .addMatcher(isPending, (state) => {
                state.isFetching = true;
                state.error = null;
            })
            .addMatcher(isFulfilled, (state) => {
                state.isFetching = false;
            })
            .addMatcher(isRejected, (state, action: any) => {
                state.isFetching = false;
                state.error = action.payload || "Something went wrong";
            });
    }
})
export default expenseSlice.reducer;